import React, { useEffect, useRef, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/solid";

const renderSortableHeader = (header) => {
  const isSorted = header.column.getIsSorted();
  const canSort = header.column.getCanSort();

  return (
    <div
      className={`
        flex items-center gap-1 
        ${canSort ? 'cursor-pointer select-none' : ''}
        ${header.column.columnDef.headerClassName || ''}
      `}
      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
    >
      {flexRender(header.column.columnDef.header, header.getContext())}
      {canSort && (
        <>
          {isSorted === "asc" && <ChevronUpIcon className="h-4 w-4 min-h-4 min-w-4" />}
          {isSorted === "desc" && <ChevronDownIcon className="h-4 w-4 min-h-4 min-w-4" />}
          {isSorted === false && (
            <ChevronUpIcon className="h-4 w-4 min-h-4 min-w-4 opacity-0 group-hover:opacity-50 transition-opacity" />
          )}
        </>
      )}
    </div>
  );
};

export const TanStackTable = ({ 
    data, 
    hasActions,
    columns,
    additionalData = {},
    overrideStickyOptions = {},
    enableSorting = false,
    onRowClick,
    compact = false
}) => {
  const { sorting, setSorting } = additionalData;
  const headerPaddingClass = compact ? 'py-2.5 px-4' : 'py-3 px-4';
  const bodyPaddingClass = compact ? 'py-2.5 px-4' : 'py-3 px-4';
  const wrapperRef = useRef(null);
  const tableHeaderRef = useRef(null);
  const tableRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const [headerWidths, setHeaderWidths] = useState([]);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [innerTranslateX, setInnerTranslateX] = useState(0);
  const [overlayStyle, setOverlayStyle] = useState({ top: 0, left: 0, width: 0 });
  const scrollerRef = useRef(null);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: enableSorting,
    state: enableSorting ? { sorting } : {},
    onSortingChange: enableSorting ? setSorting : undefined,
  });

  useEffect(() => {
    const syncHeaderWidths = () => {
      const head = tableHeaderRef.current;
      if (!head) return;
      const firstRow = head.querySelector("tr");
      if (!firstRow) return;
      const ths = Array.from(firstRow.children || []);
      const widths = ths.map((th) => {
        const rect = th.getBoundingClientRect();
        return rect.width;
      });
      setHeaderWidths(widths);
    };

    syncHeaderWidths();

    const resizeObserver = new ResizeObserver(() => {
      syncHeaderWidths();
    });
    if (tableRef.current) resizeObserver.observe(tableRef.current);
    if (wrapperRef.current) resizeObserver.observe(wrapperRef.current);
    window.addEventListener("resize", syncHeaderWidths);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", syncHeaderWidths);
    };
  }, []);

  useEffect(() => {
    const getScrollParent = (node) => {
      if (!node) return null;
      let current = node.parentElement;
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        const overflowY = style.getPropertyValue("overflow-y");
        const overflow = style.getPropertyValue("overflow");
        if (
          overflowY === "auto" ||
          overflowY === "scroll" ||
          overflow === "auto" ||
          overflow === "scroll"
        ) {
          return current;
        }
        current = current.parentElement;
      }
      return null;
    };

    const wrapper = wrapperRef.current;
    const scroller = getScrollParent(wrapper) || wrapper;
    scrollerRef.current = scroller;
    if (!wrapper || !scroller) return;

    const handleScroll = () => {
      const rect = wrapper.getBoundingClientRect();
      const headEl = tableHeaderRef.current;
      const headerHeight =
        headEl?.getBoundingClientRect()?.height != null
          ? headEl.getBoundingClientRect().height
          : 0;

      const shouldShow = rect.top < 0 && rect.bottom > headerHeight;
      setShowStickyHeader(shouldShow);

      if (shouldShow) {
        setOverlayStyle({
          top: overrideStickyOptions?.top > 0 ? overrideStickyOptions?.top : Math.max(0, 0), // fixed to top of viewport
          left: overrideStickyOptions?.left > 0 ? overrideStickyOptions?.left : Math.floor(rect.left),
          width: overrideStickyOptions?.width > 0 ? overrideStickyOptions?.width : Math.floor(rect.width),
        });
      }

      const translateX = scroller === window ? wrapper.scrollLeft : scroller.scrollLeft;
      setInnerTranslateX(Math.max(0, translateX || 0));
    };

    handleScroll();
    scroller.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div>
      <div className="relative overflow-auto w-full" ref={wrapperRef}>
        <div
          ref={stickyHeaderRef}
          className="z-20 bg-gray-50 border-b border-gray-200"
          style={{
            display: showStickyHeader ? "block" : "none",
            position: "fixed",
            top: overlayStyle.top,
            left: overlayStyle.left,
            width: overlayStyle.width,
          }}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <div key={headerGroup.id} className="bg-gray-50 border-b border-gray-200 overflow-hidden">
              <div
                className="flex"
                style={{ transform: `translateX(${-innerTranslateX}px)` }}
              >
                {headerGroup.headers.map((header, index) => (
                  <div
                    key={header.id}
                    className={`
                      group ${headerPaddingClass} flex items-center text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100
                      ${header.column.columnDef.headerTrClassName || ''}
                      ${index === headerGroup.headers.length - 1 ? 'right-0 bg-gray-50' : 'bg-gray-50'}
                    `}
                    style={{
                      width: headerWidths[index] != null ? `${headerWidths[index]}px` : undefined,
                      minWidth: headerWidths[index] != null ? `${headerWidths[index]}px` : undefined,
                      boxSizing: 'border-box',
                    }}
                    onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                  >
                    {renderSortableHeader(header)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <table ref={tableRef} className="w-full">
          <thead ref={tableHeaderRef} className="bg-gray-50 relative">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-gray-50 border-b border-gray-200">
                {headerGroup.headers.map((header, index) => (
                  <th
                    key={header.id}
                    className={`
                      group ${headerPaddingClass} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100
                      ${header.column.columnDef.headerTrClassName || ''}
                      ${index === headerGroup.headers.length - 1 ? 'right-0 bg-gray-50' : 'bg-gray-50'}
                    `}
                  >
                    {renderSortableHeader(header)}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200">
            {table.getRowModel().rows.map((row, index) => (
              <tr 
                key={row.id} 
                onClick={() => onRowClick && onRowClick(row)}
                className={`
                  border-b border-gray-200 last:border-b-0 hover:bg-blue-50 
                  cursor-pointer
                  ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                `}
              >
                {row.getVisibleCells().map((cell, cellIndex) => (
                  <td 
                    key={cell.id} 
                    className={`
                      whitespace-nowrap ${bodyPaddingClass} text-sm text-gray-600
                      ${cellIndex === row.getVisibleCells().length - 1
                        ? `sticky right-0 z-10 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`
                        : index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                    `}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
